import { Injectable, inject, Injector, runInInjectionContext } from '@angular/core';
import { Firestore, collection, doc, collectionData, addDoc, setDoc, updateDoc, deleteDoc, query, where } from '@angular/fire/firestore';
import { serverTimestamp, DocumentReference } from 'firebase/firestore';
import { Observable } from 'rxjs';
import { map, tap } from 'rxjs/operators';
import { Project, ProjectStats, StatusCount } from '../models/project.model';
import { AuthService } from './auth.service';
import { ApprovalService } from './approval.service';
import { ApprovalRequest } from '../models/approval.model';
import { DepartmentMatchingService } from './department-matching.service';
import { DepartmentSchemesService } from './department-schemes.service';
import { FirestoreFieldExecutionProjectService } from './firestore-field-execution-project.service';
import { isFieldExecutionProject, isHmTaggedProject } from '../utils/project-scope.utils';

@Injectable({ providedIn: 'root' })
export class FirestoreProjectService {
  private firestore = inject(Firestore);
  private injector = inject(Injector);
  private authService = inject(AuthService);
  private approvalService = inject(ApprovalService);
  private deptMatching = inject(DepartmentMatchingService);
  private deptSchemes = inject(DepartmentSchemesService);
  private feProjectService = inject(FirestoreFieldExecutionProjectService);
  private col = collection(this.firestore, 'projects');
  /** Bump when cleanup rules change so maintenance re-runs after deploy. */
  private static readonly LEGACY_CLEANUP_VERSION = 4;
  private legacyCleanupVersion = 0;

  // Helper to run Firestore calls in proper injection context (AngularFire 20 compatibility)
  private injectCtx<T>(fn: () => T): T {
    return runInInjectionContext(this.injector, fn);
  }

  // --------------------------------------------------------------------------
  // Real‑time streams (unchanged)
  // --------------------------------------------------------------------------
  private readonly allProjects$ = collectionData(this.col, { idField: 'id' }) as Observable<Project[]>;
  /** Every consumer of `projects` shares this stream (cleanup runs once data arrives). */
  private readonly projectsStream$ = this.allProjects$.pipe(
    tap(projects => {
      if (projects.length === 0) return;
      if (this.legacyCleanupVersion >= FirestoreProjectService.LEGACY_CLEANUP_VERSION) return;
      this.legacyCleanupVersion = FirestoreProjectService.LEGACY_CLEANUP_VERSION;
      this.cleanupLegacyProjects(projects).catch(() => { /* non-fatal */ });
    })
  );
  private stuckProjects$ = collectionData(
    query(this.col, where('status', '==', 'Stuck')),
    { idField: 'id' }
  ) as Observable<Project[]>;

  getProjects$(): Observable<Project[]> {
    return this.projectsStream$;
  }

  /** True when a row is a real HM Decision project (master dept or HM tag — not junk imports). */
  isValidHmProject(p: Project): boolean {
    if (isFieldExecutionProject(p)) return false;
    if (isHmTaggedProject(p)) return true;
    return !!this.deptMatching.resolveDepartment(p, this.deptSchemes.getDepartments());
  }

  /** HM Decision projects only — excludes Field Execution and unmapped junk rows. */
  getHmProjects$(): Observable<Project[]> {
    return this.projectsStream$.pipe(map(projects => projects.filter(p => this.isValidHmProject(p))));
  }

  getProjectById$(id: string): Observable<Project | undefined> {
    return this.projectsStream$.pipe(map(projects => projects.find(p => p.id === id)));
  }

  getStuckProjects$(): Observable<Project[]> {
    return this.stuckProjects$;
  }

  getStats$(): Observable<ProjectStats> {
    return this.getHmProjects$().pipe(
      map(projects => ({
        totalProjects: projects.length,
        totalSanctioned: projects.reduce((s, p) => s + (p.cost ?? 0), 0),
        totalSpent: projects.reduce((s, p) => s + (p.spent ?? 0), 0),
        stuckCount: projects.filter(p => p.status === 'Stuck').length,
        avgProgress: projects.length ? Math.round(projects.reduce((s, p) => s + (p.physical ?? 0), 0) / projects.length) : 0,
      }))
    );
  }

  getStatusCounts$(): Observable<StatusCount> {
    return this.getHmProjects$().pipe(
      map(projects => ({
        Completed: projects.filter(p => p.status === 'Completed').length,
        'In Progress': projects.filter(p => p.status === 'In Progress').length,
        Stuck: projects.filter(p => p.status === 'Stuck').length,
        Planned: projects.filter(p => p.status === 'Planned').length,
      }))
    );
  }

  // --------------------------------------------------------------------------
  // Mutations – now go through approval workflow for operators
  // --------------------------------------------------------------------------
  private async isOperator(): Promise<boolean> {
    return this.authService.isOperator();
  }

  private async isApprover(): Promise<boolean> {
    return this.authService.isApprover();
  }

  /** Admin, Minister, and OSD all have direct-write authority. */
  private async canWriteDirectly(): Promise<boolean> {
    return this.authService.isAdmin() || this.authService.isMinister() || this.authService.isOsd();
  }

  async createProject(project: Omit<Project, 'id'>): Promise<string> {
    const clean = Object.fromEntries(
      Object.entries({ ...project, projectScope: 'hm' as const }).filter(([, v]) => v !== undefined)
    );
    
    if (await this.isOperator()) {
      // Strip undefined fields — Firestore rejects them in a payload map.
      const safePayload = Object.fromEntries(
        Object.entries(clean).filter(([, v]) => v !== undefined && v !== null)
      );
      await this.approvalService.enqueueChange({
        targetCollection: 'projects',
        operation: 'create',
        payload: safePayload,
        remarks: 'New project creation request'
      });
      return 'pending';
    }

    if (!(await this.canWriteDirectly())) {
      throw new Error('Unauthorized: Only Admins or Ministers can write directly to projects.');
    }

    // Admin/Minister path – immediate write
    const ref = await this.injectCtx(() => addDoc(this.col, { ...clean, createdAt: serverTimestamp(), updatedAt: serverTimestamp() }));
    return (ref as DocumentReference).id;
  }

  async updateProject(id: string, updates: Partial<Omit<Project, 'id'>>): Promise<void> {
    const clean = Object.fromEntries(Object.entries(updates).filter(([, v]) => v !== undefined));
    
    if (await this.isOperator()) {
      const safePayload = Object.fromEntries(
        Object.entries(clean).filter(([, v]) => v !== undefined && v !== null)
      );
      await this.approvalService.enqueueChange({
        targetCollection: 'projects',
        targetDocId: id,
        operation: 'update',
        payload: safePayload,
        remarks: 'Project update request'
      });
      return;
    }

    // Approvers can edit projects they've already approved (post-approval housekeeping).
    // Firestore rules already permit this — extend the service to match.
    if (!(await this.canWriteDirectly()) && !this.authService.isApprover()) {
      throw new Error('Unauthorized: Only Admins, Ministers, OSD or Approvers can update projects directly.');
    }

    await this.injectCtx(() => updateDoc(doc(this.col, id), { ...clean, updatedAt: serverTimestamp() }));
  }

  async setProject(id: string, data: any): Promise<void> {
    const clean = Object.fromEntries(Object.entries(data).filter(([, v]) => v !== undefined));

    if (await this.isOperator()) {
      await this.approvalService.enqueueChange({
        targetCollection: 'projects',
        targetDocId: id,
        operation: 'update',
        payload: clean,
        remarks: 'Project update/seed request'
      });
      return;
    }

    if (!(await this.canWriteDirectly())) {
      throw new Error('Unauthorized: Only Admins or Ministers can directly update project details.');
    }

    await this.injectCtx(() => setDoc(doc(this.col, id), { ...clean, updatedAt: serverTimestamp() }, { merge: true }));
  }

  async deleteProject(id: string): Promise<void> {
    if (await this.isOperator()) {
      await this.approvalService.enqueueChange({
        targetCollection: 'projects',
        targetDocId: id,
        operation: 'delete',
        payload: { id },
        remarks: 'Project deletion request'
      });
      return;
    }

    // Approvers can delete projects they've previously approved.
    if (!(await this.canWriteDirectly()) && !this.authService.isApprover()) {
      throw new Error('Unauthorized: Only Admins, Ministers, OSD or Approvers can delete projects.');
    }

    await this.injectCtx(() => deleteDoc(doc(this.col, id)));
  }

  /**
   * One-time maintenance: migrate FE rows out of `projects`, then delete junk
   * rows that are not valid HM (fake dept labels, orphans, import leftovers).
   */
  async cleanupLegacyProjects(allProjects: Project[]): Promise<number> {
    const migrated = await this.feProjectService
      .migrateFromHmCollection(allProjects, { force: true })
      .catch(() => 0);

    const isDemoSeedProject = (p: Project): boolean =>
      (p.name || '').trim().toLowerCase() === 'test';

    const toDelete = allProjects.filter(
      p => p.id && (!this.isValidHmProject(p) || isDemoSeedProject(p))
    );
    if (toDelete.length === 0) {
      if (migrated > 0) {
        console.info(`[HM Projects] Migrated ${migrated} Field Execution row(s) out of projects collection.`);
      }
      return 0;
    }

    let removed = 0;
    await Promise.all(
      toDelete.map(async p => {
        try {
          await this.injectCtx(() => deleteDoc(doc(this.col, p.id)));
          removed++;
        } catch (err) {
          console.warn('[HM Projects] Could not remove legacy row:', p.name || p.id, err);
        }
      })
    );
    if (removed > 0 || migrated > 0) {
      console.info(
        `[HM Projects] Cleanup: migrated ${migrated}, removed ${removed} non-HM row(s) from projects collection.`
      );
    }
    return removed;
  }

  // --------------------------------------------------------------------------
  // Helper utilities (unchanged)
  // --------------------------------------------------------------------------
  async seedProjects(projects: Project[]): Promise<void> {
    await Promise.all(
      projects.map(({ id, ...data }) => {
        const clean = Object.fromEntries(Object.entries(data).filter(([, v]) => v !== undefined));
        return this.injectCtx(() => setDoc(doc(this.col, id), { ...clean, createdAt: serverTimestamp(), updatedAt: serverTimestamp() }));
      })
    );
  }

}
